import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { T, REGIMENES_RECEPTOR, USOS, datosDelError } from "../../lib/cfdi";

// Ficha fiscal del paciente (CL.4 C). Se usa en la ficha del paciente y, cuando falta, dentro
// de «Facturar». La validación de verdad la hace el backend (422 con su `detail`); aquí sólo
// se evita mandar un formulario vacío. Textos: lib/cfdi.js (# Copy aprobado por Jesús (4 Oct 2026)).
const campo = "mt-1 h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-electric";

export default function FichaFiscal({ patientId, onGuardada, compacta = false }) {
  const [f, setF] = useState({ rfc: "", razon_social: "", regimen: "605", cp: "", uso_cfdi: "D01", email_factura: "" });
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    let vivo = true;
    api.get(`/patients/${encodeURIComponent(patientId)}/fiscal`)
      .then((r) => { if (vivo && r.data?.fiscal) setF((x) => ({ ...x, ...r.data.fiscal })); })
      .catch((e) => { if (vivo) setError(datosDelError(e).detail); })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [patientId]);

  const cambia = (k) => (e) => { setOk(false); setF((x) => ({ ...x, [k]: e.target.value })); };

  const guardar = async () => {
    setError(null);
    setGuardando(true);
    try {
      const cuerpo = {
        rfc: (f.rfc || "").trim().toUpperCase(), razon_social: (f.razon_social || "").trim(),
        regimen: f.regimen, cp: (f.cp || "").trim(), uso_cfdi: f.uso_cfdi,
        email_factura: (f.email_factura || "").trim(),
      };
      const { data } = await api.put(`/patients/${encodeURIComponent(patientId)}/fiscal`, cuerpo);
      setF((x) => ({ ...x, ...data.fiscal }));
      setOk(true);
      onGuardada?.(data.fiscal);
    } catch (e) {
      setError(datosDelError(e).detail);
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) return <div className="text-sm text-slate-400">…</div>;

  return (
    <div data-testid="ficha-fiscal">
      {!compacta && <p className="text-xs text-slate-500 mb-3">{T.fiscalSub}</p>}
      <div className={`grid gap-3 ${compacta ? "sm:grid-cols-2" : ""}`}>
        <label className="text-xs font-medium text-slate-600">{T.rfc}
          <input className={`${campo} uppercase`} maxLength={13} value={f.rfc} onChange={cambia("rfc")} data-testid="fiscal-rfc" />
        </label>
        <label className="text-xs font-medium text-slate-600">{T.razonSocialPaciente}
          <input className={campo} value={f.razon_social} onChange={cambia("razon_social")} data-testid="fiscal-razon" />
        </label>
        <label className="text-xs font-medium text-slate-600">{T.regimen}
          <select className={campo} value={f.regimen} onChange={cambia("regimen")} data-testid="fiscal-regimen">
            {REGIMENES_RECEPTOR.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
          </select>
        </label>
        <label className="text-xs font-medium text-slate-600">{T.cp}
          <input className={campo} maxLength={5} value={f.cp} onChange={cambia("cp")} data-testid="fiscal-cp" />
        </label>
        <label className="text-xs font-medium text-slate-600">{T.usoCfdi}
          <select className={campo} value={f.uso_cfdi} onChange={cambia("uso_cfdi")} data-testid="fiscal-uso">
            {USOS.map((u) => <option key={u.code} value={u.code}>{u.label}</option>)}
          </select>
        </label>
        <label className="text-xs font-medium text-slate-600">{T.emailFactura}
          <input className={campo} type="email" value={f.email_factura} onChange={cambia("email_factura")} data-testid="fiscal-email" />
        </label>
      </div>
      {error && <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600" data-testid="fiscal-error">{error}</p>}
      {ok && <p className="mt-2 text-sm text-emerald-700" data-testid="fiscal-ok">{T.fiscalGuardado}</p>}
      <button type="button" onClick={guardar} disabled={guardando}
              className="mt-3 inline-flex items-center text-sm font-medium bg-brand-deep text-white px-3 py-1.5 rounded-lg hover:bg-slate-800 disabled:opacity-60"
              data-testid="fiscal-guardar">
        {guardando ? T.guardando : T.guardar}
      </button>
    </div>
  );
}
